import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  MenuItem, 
  Select, 
  InputLabel, 
  FormControl, 
  Stack, 
  Checkbox,
  InputAdornment,
  Chip,
  Card,
  CardContent,
  Grid,
  Divider,
  Alert,
  Tooltip
} from '@mui/material';
import { 
  Add, 
  Edit, 
  Delete, 
  Visibility, 
  ArrowUpward, 
  ArrowDownward,
  Search,
  FilterList,
  Refresh
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import Autocomplete from '@mui/material/Autocomplete';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import CodeSearchDropdown from './CodeSearchDropdown';
import apiService from '../services/api';

interface WorkflowFeatureConfig {
  enabledTabs: string[];
  optionalModules?: string[];
  uiMode?: string;
}

interface SpecimenClassConfig {
  _id?: string;
  id?: string | number;
  label: string;
  enabledFeatures: string[];
  workflow: WorkflowFeatureConfig;
  useCase: string;
  sections?: string[];
  apCaseTemplateIds?: (string | number)[];
  ap_case_template_ids?: (string | number)[];
  report_template_names?: string[];
  groupId?: string | number;
  type?: string;
}

interface SpecimenClassGroup {
  id: number;
  name: string;
}

const emptyClass: SpecimenClassConfig = {
  label: '',
  enabledFeatures: [],
  workflow: { enabledTabs: [], optionalModules: [], uiMode: '' },
  useCase: '',
};

const SpecimenClassAdmin: React.FC = () => {
  const [classes, setClasses] = useState<SpecimenClassConfig[]>([]);
  const [filteredClasses, setFilteredClasses] = useState<SpecimenClassConfig[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SpecimenClassConfig | null>(null);
  const [form, setForm] = useState<SpecimenClassConfig>(emptyClass);
  const [error, setError] = useState<string | null>(null);
  const [groups, setGroups] = useState<SpecimenClassGroup[]>([]);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [sectionsDialogOpen, setSectionsDialogOpen] = useState(false);
  const [sections, setSections] = useState<any[]>([]);
  const [sectionForm, setSectionForm] = useState<any>({ name: '', description: '', fields: '' });
  const [editingSection, setEditingSection] = useState<any>(null);
  const [sectionError, setSectionError] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<any>(null);
  const [previewSection, setPreviewSection] = useState<any>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editFieldsDialogOpen, setEditFieldsDialogOpen] = useState(false);
  const [editFieldsSection, setEditFieldsSection] = useState<any>(null);
  const [editFields, setEditFields] = useState<any[]>([]);
  const [classPreviewOpen, setClassPreviewOpen] = useState(false);
  const [classPreview, setClassPreview] = useState<any>(null);
  const [sectionSortAsc, setSectionSortAsc] = useState(true);
  const [apCaseTemplates, setApCaseTemplates] = useState<any[]>([]);
  const [selectedApCaseTemplateIds, setSelectedApCaseTemplateIds] = useState<string[]>([]);
  const [reportTemplateDialogOpen, setReportTemplateDialogOpen] = useState(false);
  const [editingReportTemplate, setEditingReportTemplate] = useState<any>(null);
  const [reportTemplateForm, setReportTemplateForm] = useState({ name: '', description: '' });
  const [reportTemplateError, setReportTemplateError] = useState<string | null>(null);
  const [reportTemplateFormOpen, setReportTemplateFormOpen] = useState(false);
  const [allParts, setAllParts] = useState<any[]>([]);
  const [selectedPartIds, setSelectedPartIds] = useState<number[]>([]);
  const [partsListDialogOpen, setPartsListDialogOpen] = useState(false);
  const [partDrawerOpen, setPartDrawerOpen] = useState(false);
  const [editingPart, setEditingPart] = useState<any>(null);
  const [partForm, setPartForm] = useState<{ part_name: string; description: string; collection_method: string[] }>({
    part_name: '',
    description: '',
    collection_method: [],
  });
  const [partError, setPartError] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<'label' | 'group'>('label');
  const [sortAsc, setSortAsc] = useState(true);
  const [sectionsDropdownOpen, setSectionsDropdownOpen] = useState(false);
  const [sectionsDropdownSortAsc, setSectionsDropdownSortAsc] = useState(true);
  const [selectRef, setSelectRef] = useState<React.RefObject<HTMLSelectElement> | null>(null);
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);
  const [reorderSections, setReorderSections] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const collectionMethods = [
    'Excision', 'Punch Biopsy', 'Shave Biopsy', 'Resection', 'Surgical Excision', 'Appendectomy', 'Core Needle Biopsy', 'FNA', 'Aspiration', 'Core Biopsy', 'Thoracentesis', 'Fluid Aspiration', 'Paracentesis', 'Fine Needle Aspiration (Ultrasound-guided or Palpation-guided)', 'Voided urine', 'Catheterized specimen', 'Lumbar puncture', 'Bronchial Brushing', 'Transbronchial Biopsy', 'Lumpectomy', 'Pap Smear', 'Liquid-Based Cytology', 'Conventional Smear', 'Surgical Delivery', 'Vaginal Delivery', 'Dissection (Internal)', 'Catheter Removal', 'Cholecystectomy'
  ];

  // Filter classes based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredClasses(classes);
      return;
    }

    const filtered = classes.filter(cls => {
      const searchLower = searchTerm.toLowerCase();
      const className = cls.label?.toLowerCase() || '';
      const groupName = (Array.isArray(groups) ? groups.find(g => g.id === (cls as any).groupId)?.name?.toLowerCase() : '') || '';
      const type = (cls as any).type?.toLowerCase() || '';
      
      return className.includes(searchLower) || 
             groupName.includes(searchLower) || 
             type.includes(searchLower);
    });
    
    setFilteredClasses(filtered);
  }, [searchTerm, classes, groups]);

  const fetchClasses = async () => {
    setLoading(true);
    try {
      const data = await apiService.getSpecimenClasses();
      // Map backend data to expected frontend shape
      const mapped = (Array.isArray(data) ? data : []).map((row: any) => ({
        ...row,
        enabledFeatures: row.enabledFeatures ? (() => {
          try { return JSON.parse(row.enabledFeatures); } catch { return []; }
        })() : [],
        workflow: {
          enabledTabs: row.workflowEnabledTabs ? (() => {
            try { return JSON.parse(row.workflowEnabledTabs); } catch { return []; }
          })() : [],
          optionalModules: row.workflowOptionalModules ? (() => {
            try { return JSON.parse(row.workflowOptionalModules); } catch { return []; }
          })() : [],
          uiMode: row.workflowUiMode || '',
        },
        useCase: row.useCase || '',
        // Map sections from the junction table
        sections: (row.specimenClassTemplateSections_on_specimenClassTemplate || []).map((s: any) => String(s.sectionId)),
      }));
      setClasses(mapped);
      setFilteredClasses(mapped);
    } catch (e) {
      console.error('Error fetching specimen classes:', e);
      setClasses([]);
      setFilteredClasses([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const data = await apiService.getSpecimenClassGroups();
      setGroups(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Error fetching specimen class groups:', e);
      setGroups([]);
    }
  };

  const fetchSections = async () => {
    try {
      const data = await apiService.getSpecimenClassSections();
      setSections(data);
    } catch (e) {
      console.error('Error fetching specimen class sections:', e);
      setSections([]);
    }
  };

  const fetchApCaseTemplates = async () => {
    try {
      const res = await fetch('/api/ap-case-templates');
      const data = await res.json();
      setApCaseTemplates(data);
    } catch {
      setApCaseTemplates([]);
    }
  };

  const fetchMappedApCaseTemplates = async (specimenClassId: string) => {
    try {
      const data = await apiService.getApCaseTemplatesForSpecimenClass(specimenClassId);
      setSelectedApCaseTemplateIds(data.map((tpl: any) => String(tpl.id)));
    } catch (error) {
      console.error('Error fetching mapped AP case templates:', error);
      setSelectedApCaseTemplateIds([]);
    }
  };

  const fetchAllParts = async () => {
    try {
      const data = await apiService.getSpecimenParts();
      setAllParts(data);
    } catch (error) {
      console.error('Error fetching specimen parts:', error);
      setAllParts([]);
    }
  };

  const fetchSelectedParts = async (templateId: string | number) => {
    try {
      const data = await apiService.getSpecimenClassParts(String(templateId));
      setSelectedPartIds(data.map((p: any) => p.id));
    } catch (error) {
      console.error('Error fetching selected parts:', error);
      setSelectedPartIds([]);
    }
  };

  useEffect(() => {
    fetchClasses();
    fetchGroups();
    fetchSections();
    fetchAllParts();
  }, []);



  const handleOpenDialog = async (cls?: SpecimenClassConfig) => {
    fetchSections();
    await fetchApCaseTemplates();
    await fetchAllParts();
    setEditing(cls || null);
    const classIdRaw = cls?._id || cls?.id;
    const classId = classIdRaw ? String(classIdRaw) : undefined;
    if (cls && classId) {
      // Lazy load sections for this template
      try {
        const data = await apiService.getSpecimenClassById(classId);
        // If backend returns sections as array of objects with sectionId and orderNumOnTemplate
        if (Array.isArray(data.specimenClassTemplateSections_on_specimenClassTemplate) && data.specimenClassTemplateSections_on_specimenClassTemplate.length) {
          // Sort by orderNumOnTemplate
          const sorted = [...data.specimenClassTemplateSections_on_specimenClassTemplate].sort((a, b) => (a.orderNumOnTemplate ?? 0) - (b.orderNumOnTemplate ?? 0));
          setForm({ ...cls, sections: sorted.map(s => String(s.sectionId)) });
        } else {
          setForm({ ...cls, sections: (data.sections || []).map(String) });
        }
        await fetchMappedApCaseTemplates(classId);
        await fetchSelectedParts(classId);
      } catch (error) {
        console.error('Error fetching specimen class details:', error);
        // Fallback to using the original cls data
        setForm({ ...cls });
      }
    } else {
      setForm(emptyClass);
      setSelectedApCaseTemplateIds([]);
      setSelectedPartIds([]);
    }
    setDialogOpen(true);
    setError(null);
  };
  const handleCloseDialog = () => { setDialogOpen(false); setEditing(null); setForm(emptyClass); };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleArrayChange = (field: keyof SpecimenClassConfig, value: string) => {
    setForm(f => ({ ...f, [field]: value.split(',').map(s => s.trim()).filter(Boolean) }));
  };

  const handleWorkflowChange = (wfField: keyof WorkflowFeatureConfig, value: string) => {
    setForm(f => ({ ...f, workflow: { ...f.workflow, [wfField]: value.split(',').map(s => s.trim()).filter(Boolean) } }));
  };

  const handleSubmit = async () => {
    setError(null);
    try {
      const classId = editing?._id || editing?.id;
      // Send sections as array of {section_id, order_num_on_template}
      const sectionsPayload = (form.sections || []).map((sid, idx) => ({
        section_id: Number(sid),
        order_num_on_template: idx
      }));
      
      let savedClassId = editing?._id || editing?.id;
      if (editing && classId) {
        // Update existing specimen class
        await apiService.updateSpecimenClass(String(classId), {
          ...form, 
          sections: sectionsPayload,
        });
      } else {
        // Create new specimen class
        const data = await apiService.createSpecimenClass({
          ...form, 
          sections: sectionsPayload,
        });
        savedClassId = data.id || data._id || data.insertedId;
      }
      
      if (savedClassId) {
        // Remove all previous mappings first (optional, backend should handle idempotency)
        await fetch(`/api/ap-case-templates/unlink-all`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ specimenClassTemplateId: savedClassId }),
        });
        // Add new mappings
        for (const tplId of selectedApCaseTemplateIds) {
          await fetch(`/api/ap-case-templates/${tplId}/link`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ specimenClassTemplateId: savedClassId }),
          });
        }
        // Save parts mapping
        await fetch(`/api/specimen-classes/${savedClassId}/parts`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ partIds: selectedPartIds }),
        });
      }
      handleCloseDialog();
      fetchClasses();
    } catch (e) {
      console.error('Error saving specimen class:', e);
      setError('Error saving template');
    }
  };

  const handleDelete = async (id?: string) => {
    if (!id) return;
    if (!window.confirm('Delete this template?')) return;
    try {
      await fetch(`/api/specimen-classes/${id}`, { method: 'DELETE' });
      fetchClasses();
    } catch (e) { /* ignore */ }
  };

  const handleAddGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      await fetch('/api/specimen-classes/groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newGroupName.trim() })
      });
      setNewGroupName('');
      setGroupDialogOpen(false);
      fetchGroups();
    } catch (e) {}
  };

  const handleOpenSectionsDialog = () => {
    fetchSections();
    setSectionsDialogOpen(true);
  };
  const handleCloseSectionsDialog = () => {
    setSectionsDialogOpen(false);
    setEditingSection(null);
    setSectionForm({ name: '', description: '', fields: '' });
    setSectionError(null);
  };
  const handleSectionFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSectionForm((f: any) => ({ ...f, [e.target.name]: e.target.value }));
  };
  const handleEditSection = (section: any) => {
    setEditFieldsSection(section);
    let fields: any[] = [];
    try {
      fields = typeof section.fields === 'string' ? JSON.parse(section.fields) : section.fields || [];
    } catch {
      fields = [];
    }
    // Map data_type for dropdown and freeform
    fields = fields.map((f: any) => {
      const fieldName = String(f.field_name || '').toLowerCase();
      if (fieldName.includes('icd')) return { ...f, data_type: 'ICD_Code' };
      if (fieldName.includes('cpt')) return { ...f, data_type: 'CPT_Code' };
      if (fieldName.includes('snomed')) return { ...f, data_type: 'SNOMED_Code' };
      if (fieldName.includes('numeric')) return { ...f, data_type: 'numeric' };
      let dt = String(f.data_type || '').toLowerCase();
      if (dt.includes('dropdown')) return { ...f, data_type: 'dropdown' };
      if (dt.includes('freeform')) return { ...f, data_type: 'text' };
      if (dt === 'free text') return { ...f, data_type: 'text' };
      if (dt === 'boolean (checkbox)') return { ...f, data_type: 'dropdown' };
      if (dt === 'date picker' || dt === 'date picker') return { ...f, data_type: 'datetime picker' };
      return f;
    });
    setEditFields(fields);
    setEditFieldsDialogOpen(true);
  };
  const handleDeleteSection = async (id: number) => {
    if (!window.confirm('Delete this section?')) return;
    await fetch(`/api/specimen-classes/sections/${id}`, { method: 'DELETE' });
    fetchSections();
  };
  const handleSectionSubmit = async () => {
    setSectionError(null);
    try {
      const method = editingSection ? 'PUT' : 'POST';
      const url = editingSection ? `/api/specimen-classes/sections/${editingSection.id}` : '/api/specimen-classes/sections';
      let fields = [];
      try {
        fields = JSON.parse(sectionForm.fields);
      } catch (error) {
        console.error('Error parsing sectionForm.fields JSON:', error);
        console.log('Raw sectionForm.fields value:', sectionForm.fields);
        setSectionError('Invalid JSON in fields. Please check the format.');
        return;
      }
      const body = {
        name: sectionForm.name,
        description: sectionForm.description,
        fields: fields,
      };
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed to save section');
      handleCloseSectionsDialog();
      fetchSections();
    } catch (e) {
      setSectionError('Error saving section (check JSON fields)');
    }
  };
  const handlePreviewSection = (section: any) => {
    setPreviewSection(section);
    setPreviewOpen(true);
  };

  const handleFieldChange = (idx: number, key: string, value: any) => {
    setEditFields(fields => fields.map((f, i) => i === idx ? { ...f, [key]: value } : f));
  };
  const handleAddField = () => {
    setEditFields(fields => [...fields, { field_name: '', data_type: '', required: false }]);
  };
  const handleRemoveField = (idx: number) => {
    setEditFields(fields => fields.filter((_, i) => i !== idx));
  };
  const handleSaveFields = async () => {
    try {
      const body = {
        ...editFieldsSection,
        fields: editFields,
      };
      const res = await fetch(`/api/specimen-classes/sections/${editFieldsSection.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed to save section');
      setEditFieldsDialogOpen(false);
      fetchSections();
    } catch (e) {
      alert('Error saving section');
    }
  };

  const handleClassPreview = (cls: any) => {
    setClassPreview(cls);
    setClassPreviewOpen(true);
  };

  const handleOpenReportTemplateForm = (tpl?: any) => {
    setEditingReportTemplate(tpl || null);
    setReportTemplateForm(tpl ? { name: tpl.name, description: tpl.description } : { name: '', description: '' });
    setReportTemplateError(null);
    setReportTemplateFormOpen(true);
  };
  const handleCloseReportTemplateForm = () => {
    setEditingReportTemplate(null);
    setReportTemplateForm({ name: '', description: '' });
    setReportTemplateError(null);
    setReportTemplateFormOpen(false);
  };
  const handleReportTemplateFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReportTemplateForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };
  const handleSaveReportTemplate = async () => {
    setReportTemplateError(null);
    try {
      const method = editingReportTemplate ? 'PUT' : 'POST';
      const url = editingReportTemplate ? `/api/ap-case-templates/${editingReportTemplate.id}` : '/api/ap-case-templates';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reportTemplateForm),
      });
      if (!res.ok) throw new Error('Failed to save');
      await fetchApCaseTemplates();
      handleCloseReportTemplateForm();
    } catch (e) {
      setReportTemplateError('Error saving report template');
    }
  };
  const handleDeleteReportTemplate = async (tpl: any) => {
    if (!window.confirm('Delete this report template?')) return;
    try {
      await fetch(`/api/ap-case-templates/${tpl.id}`, { method: 'DELETE' });
      await fetchApCaseTemplates();
    } catch (e) {
      alert('Error deleting report template');
    }
  };

  const handleOpenPartsDialog = () => {
    setPartsListDialogOpen(true);
    fetchAllParts();
  };
  const handleClosePartsListDialog = () => {
    setPartsListDialogOpen(false);
  };
  // Helper function to safely parse collection method JSON
  const parseCollectionMethod = (value: any): string[] => {
    if (!value) return [];
    
    // If it's already an array, return it
    if (Array.isArray(value)) {
      return value;
    }
    
    // If it's not a string, return empty array
    if (typeof value !== 'string') {
      return [];
    }
    
    try {
      // Try to parse as JSON
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Error parsing collectionMethod JSON:', error);
      console.log('Raw collectionMethod value:', value);
      
      // Try to extract array-like strings as fallback
      try {
        const cleaned = value
          .replace(/^\[|\]$/g, '') // Remove outer brackets if present
          .split(',')
          .map((item: string) => item.trim().replace(/^["']|["']$/g, '')) // Remove quotes
          .filter((item: string) => item.length > 0);
        return cleaned;
      } catch (fallbackError) {
        console.error('Fallback parsing also failed:', fallbackError);
        return [];
      }
    }
  };

  const handleEditPart = (part: any) => {
    setEditingPart(part);
    const collectionMethod = parseCollectionMethod(part.collectionMethod);
    setPartForm({ part_name: part.partName, description: part.description || '', collection_method: collectionMethod });
    setPartError(null);
    setPartDrawerOpen(true);
  };
  const handleAddPart = () => {
    setEditingPart(null);
    setPartForm({ part_name: '', description: '', collection_method: [] });
    setPartError(null);
    setPartDrawerOpen(true);
  };
  const handleClosePartsDialog = () => {
    setPartDrawerOpen(false);
    setEditingPart(null);
    setPartForm({ part_name: '', description: '', collection_method: [] });
    setPartError(null);
  };
  const handlePartFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPartForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handlePartCollectionMethodChange = (methods: string[]) => {
    setPartForm(f => ({ ...f, collection_method: methods }));
  };
  const handleSavePart = async () => {
    setPartError(null);
    try {
      const method = editingPart ? 'PUT' : 'POST';
      const url = editingPart ? `/api/specimen-parts/${editingPart.id}` : '/api/specimen-parts';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({
          partName: partForm.part_name,
          description: partForm.description,
          collectionMethod: partForm.collection_method
        }),
      });
      if (!res.ok) throw new Error('Failed to save');
      handleClosePartsDialog();
      fetchAllParts();
    } catch (e) {
      console.error('Error saving part:', e);
      setPartError('Error saving part');
    }
  };

  const handleDeletePart = async (id: number) => {
    if (!window.confirm('Delete this part?')) return;
    try {
      const res = await fetch(`/api/specimen-parts/${id}`, { 
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) throw new Error('Failed to delete');
      fetchAllParts();
    } catch (error) {
      console.error('Error deleting part:', error);
    }
  };



  const handleOpenReorderDialog = () => {
    // Get selected sections in current order
    const selectedSections = (form.sections || [])
      .map((sid: string) => sections.find(s => String(s.id) === String(sid)))
      .filter(Boolean);
    setReorderSections(selectedSections);
    setReorderDialogOpen(true);
  };

  const moveSection = (idx: number, dir: -1 | 1) => {
    setReorderSections(secs => {
      const arr = [...secs];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= arr.length) return arr;
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return arr;
    });
  };

  const handleSaveReorder = () => {
    setForm(f => ({
      ...f,
      sections: reorderSections.map(s => String(s.id)),
    }));
    setReorderDialogOpen(false);
  };

  const handleRefresh = () => {
    fetchClasses();
    fetchGroups();
    fetchSections();
    fetchAllParts();
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header Section */}
      <Card sx={{ mb: 3, boxShadow: 3 }}>
        <CardContent>
          <Typography variant="h4" gutterBottom sx={{ color: '#1976d2', fontWeight: 600 }}>
            Specimen Class Templates Admin
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Manage specimen class templates, sections, and configurations
          </Typography>
          
          {/* Search Bar */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by class name, group, or type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm('')}
                      edge="end"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'white',
                }
              }}
            />
          </Box>

          {/* Action Buttons */}
          <Grid container spacing={2}>
            <Grid item>
              <Button 
                variant="contained" 
                startIcon={<Add />} 
                onClick={() => handleOpenDialog()}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3
                }}
              >
                Add New Template
              </Button>
            </Grid>
            <Grid item>
              <Button 
                variant="outlined" 
                onClick={handleOpenSectionsDialog}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3
                }}
              >
                Manage Sections
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                color="secondary"
                onClick={async () => {
                  await fetchApCaseTemplates();
                  setReportTemplateDialogOpen(true);
                }}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3
                }}
              >
                Manage Report Templates
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                onClick={handleOpenPartsDialog}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  borderColor: 'purple',
                  color: 'purple',
                  '&:hover': {
                    borderColor: 'purple',
                    backgroundColor: 'rgba(156, 39, 176, 0.04)'
                  }
                }}
              >
                Manage Specimen Parts
              </Button>
            </Grid>
            <Grid item>
              <Tooltip title="Refresh data">
                <IconButton 
                  onClick={handleRefresh}
                  disabled={loading}
                  sx={{ 
                    border: '1px solid #ddd',
                    '&:hover': { backgroundColor: '#f0f0f0' }
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Results Summary */}
      {searchTerm && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          Showing {filteredClasses.length} of {classes.length} specimen classes matching "{searchTerm}"
        </Alert>
      )}

      {/* Main Table */}
      <Card sx={{ boxShadow: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell
                  onClick={() => {
                    if (sortColumn === 'label') setSortAsc(a => !a);
                    else { setSortColumn('label'); setSortAsc(true); }
                  }}
                  sx={{ 
                    cursor: 'pointer', 
                    fontWeight: 700,
                    color: '#1976d2',
                    '&:hover': { backgroundColor: '#e3f2fd' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterList fontSize="small" />
                    Specimen Class Name 
                    {sortColumn === 'label' ? (sortAsc ? '▲' : '▼') : ''}
                  </Box>
                </TableCell>
                <TableCell
                  onClick={() => {
                    if (sortColumn === 'group') setSortAsc(a => !a);
                    else { setSortColumn('group'); setSortAsc(true); }
                  }}
                  sx={{ 
                    cursor: 'pointer', 
                    fontWeight: 700,
                    color: '#1976d2',
                    '&:hover': { backgroundColor: '#e3f2fd' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterList fontSize="small" />
                    Group 
                    {sortColumn === 'group' ? (sortAsc ? '▲' : '▼') : ''}
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Sections</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Report Templates</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredClasses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {loading ? 'Loading...' : searchTerm ? 'No specimen classes found matching your search.' : 'No specimen classes available.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                [...filteredClasses].sort((a, b) => {
                  if (sortColumn === 'label') {
                    return sortAsc
                      ? String(a.label).localeCompare(String(b.label))
                      : String(b.label).localeCompare(String(a.label));
                  } else if (sortColumn === 'group') {
                    const groupA = (Array.isArray(groups) ? groups.find(g => g.id === a.groupId)?.name : '') || '';
                    const groupB = (Array.isArray(groups) ? groups.find(g => g.id === b.groupId)?.name : '') || '';
                    return sortAsc
                      ? String(groupA).localeCompare(String(groupB))
                      : String(groupB).localeCompare(String(groupA));
                  }
                  return 0;
                }).map(cls => {
                  const reportTemplateNames = cls.report_template_names || [];
                  const groupName = (Array.isArray(groups) ? groups.find(g => g.id === cls.groupId)?.name : '') || '';
                  return (
                    <TableRow 
                      key={cls._id || cls.id}
                      sx={{ 
                        '&:hover': { backgroundColor: '#f8f9fa' },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>{cls.label}</TableCell>
                      <TableCell>
                        {groupName ? (
                          <Chip 
                            label={groupName} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No group
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {cls.type && (
                          <Chip 
                            label={cls.type} 
                            size="small" 
                            color="secondary" 
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(cls.sections || []).length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              No sections
                            </Typography>
                          ) : (
                            (cls.sections || []).map(
                              (sectionId: any) => {
                                const section = sections.find(s => String(s.id) === String(sectionId));
                                return section ? (
                                  <Chip 
                                    key={sectionId}
                                    label={section.name} 
                                    size="small" 
                                    variant="outlined"
                                    sx={{ fontSize: '0.75rem' }}
                                  />
                                ) : (
                                  <Chip 
                                    key={sectionId}
                                    label={`Unknown (${sectionId})`} 
                                    size="small" 
                                    variant="outlined"
                                    color="error"
                                    sx={{ fontSize: '0.75rem' }}
                                  />
                                );
                              }
                            )
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {reportTemplateNames.map((name, idx) => (
                            <Chip 
                              key={idx}
                              label={name} 
                              size="small" 
                              color="success" 
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Edit template">
                            <IconButton 
                              onClick={() => handleOpenDialog(cls)}
                              size="small"
                              sx={{ color: '#1976d2' }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete template">
                            <IconButton 
                              onClick={() => handleDelete(String(cls._id || cls.id))}
                              size="small"
                              sx={{ color: '#d32f2f' }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Preview template">
                            <IconButton 
                              onClick={() => handleClassPreview(cls)}
                              size="small"
                              sx={{ color: '#388e3c' }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* ... existing dialogs ... */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        keepMounted={false}
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#f8f9fa', 
          borderBottom: '1px solid #e0e0e0',
          fontWeight: 600,
          color: '#1976d2'
        }}>
          {editing ? 'Edit' : 'Add'} Specimen Class Template
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2, p: 3 }}>
          <TextField
            label="Label"
            name="label"
            value={form.label}
            onChange={handleChange}
            fullWidth
            required
            inputProps={{ maxLength: 100 }}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
              }
            }}
          />
          <Stack direction="row" spacing={2} alignItems="flex-end">
            <FormControl fullWidth>
              <InputLabel id="group-label">Group</InputLabel>
              <Select
                labelId="group-label"
                value={(form as any).groupId || ''}
                label="Group"
                onChange={e => setForm(f => ({ ...f, groupId: e.target.value }))}
                required
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  }
                }}
              >
                {Array.isArray(groups) ? groups.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>) : []}
              </Select>
            </FormControl>
            <Button 
              variant="outlined" 
              onClick={() => setGroupDialogOpen(true)}
              sx={{ 
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                px: 2
              }}
            >
              Add Group
            </Button>
          </Stack>
          <TextField 
            label="Type" 
            value={(form as any).type || ''} 
            onChange={e => setForm(f => ({ ...f, type: e.target.value }))} 
            fullWidth 
            required 
            sx={{ 
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
              }
            }}
          />
          <FormControl fullWidth>
            <InputLabel id="sections-label">Sections</InputLabel>
            <Select
              labelId="sections-label"
              multiple
              value={form.sections || []}
              label="Sections"
              onChange={e => setForm(f => ({ ...f, sections: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value }))}
              renderValue={(selected) =>
                sections
                  .filter(s => selected.includes(String(s.id)))
                  .map(s => s.name)
                  .join(', ')
              }
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 400 },
                },
              }}
              ref={selectRef}
              open={sectionsDropdownOpen}
              onClose={() => setSectionsDropdownOpen(false)}
              onOpen={() => setSectionsDropdownOpen(true)}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                }
              }}
            >
              <MenuItem disabled divider style={{ fontWeight: 'bold', cursor: 'pointer' }}
                onClick={() => setSectionsDropdownSortAsc((asc: boolean) => !asc)}
              >
                Section Names {sectionsDropdownSortAsc ? '▲' : '▼'}
              </MenuItem>
              {[...sections].sort((a, b) => {
                return sectionsDropdownSortAsc
                  ? a.name.localeCompare(b.name)
                  : b.name.localeCompare(a.name);
              }).map(section => (
                <MenuItem key={section.id} value={String(section.id)}>
                  <Checkbox checked={form.sections?.includes(String(section.id))} />
                  {section.name}
                </MenuItem>
              ))}
              <MenuItem divider disabled />
              <MenuItem onClick={() => setSectionsDropdownOpen(false)} style={{ justifyContent: 'center', fontWeight: 600 }}>
                Done
              </MenuItem>
            </Select>
            <Button 
              size="small" 
              sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }} 
              onClick={handleOpenReorderDialog} 
              disabled={!(form.sections && form.sections.length > 1)}
            >
              Change Section Order
            </Button>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="ap-case-template-label">Report Templates</InputLabel>
            <Select
              labelId="ap-case-template-label"
              multiple
              value={selectedApCaseTemplateIds}
              label="Report Templates"
              onChange={e => setSelectedApCaseTemplateIds(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              renderValue={(selected) =>
                apCaseTemplates.filter((tpl: any) => selected.includes(String(tpl.id))).map((tpl: any) => tpl.name).join(', ')
              }
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                }
              }}
            >
              {apCaseTemplates.map((tpl: any) => (
                <MenuItem key={tpl.id} value={String(tpl.id)}>
                  <Checkbox checked={selectedApCaseTemplateIds.includes(String(tpl.id))} />
                  {tpl.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="parts-label">Specimen Parts</InputLabel>
            <Select
              labelId="parts-label"
              multiple
              value={selectedPartIds}
              label="Specimen Parts"
              onChange={e => setSelectedPartIds(typeof e.target.value === 'string' ? e.target.value.split(',').map(Number) : e.target.value)}
              renderValue={selected =>
                allParts.filter(p => selected.includes(p.id)).map(p => p.partName).join(', ')
              }
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                }
              }}
            >
              {allParts.map(part => (
                <MenuItem key={part.id} value={part.id}>
                  <Checkbox checked={selectedPartIds.includes(part.id)} />
                  {part.partName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {error && (
            <Alert severity="error" sx={{ borderRadius: 1.5 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{ 
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            sx={{ 
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog 
        open={groupDialogOpen} 
        onClose={() => setGroupDialogOpen(false)}
        keepMounted={false}
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#f8f9fa', 
          borderBottom: '1px solid #e0e0e0',
          fontWeight: 600,
          color: '#1976d2'
        }}>
          Add Group
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <TextField 
            label="Group Name" 
            value={newGroupName} 
            onChange={e => setNewGroupName(e.target.value)} 
            fullWidth 
            sx={{ 
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={() => setGroupDialogOpen(false)}
            sx={{ 
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleAddGroup}
            sx={{ 
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog 
        open={sectionsDialogOpen} 
        onClose={handleCloseSectionsDialog} 
        maxWidth="md" 
        fullWidth
        keepMounted={false}
      >
        <DialogTitle sx={{ m: 0, p: 2, pr: 5, position: 'relative' }}>
          Manage Sections
          <IconButton
            aria-label="close"
            onClick={handleCloseSectionsDialog}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Button variant="contained" onClick={() => {
              setEditingSection(null);
              setEditFields([]);
              setEditFieldsSection({ name: '', description: '', fields: [] });
              setEditFieldsDialogOpen(true);
            }}>Add Section</Button>
          </Box>
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    onClick={() => setSectionSortAsc((asc) => !asc)}
                    style={{ cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    Name {sectionSortAsc ? '▲' : '▼'}
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(Array.isArray(sections) ?
                  [...sections].sort((a, b) => {
                    if (!a.name) return 1;
                    if (!b.name) return -1;
                    return sectionSortAsc
                      ? a.name.localeCompare(b.name)
                      : b.name.localeCompare(a.name);
                  }) : []
                ).map(section => (
                  <TableRow key={section.id} hover onClick={() => setSelectedSection(section)} selected={selectedSection?.id === section.id} style={{ cursor: 'pointer' }}>
                    <TableCell>{section.name}</TableCell>
                    <TableCell>
                      <IconButton onClick={e => { e.stopPropagation(); handleEditSection(section); }}><Edit /></IconButton>
                      <IconButton onClick={e => { e.stopPropagation(); handleDeleteSection(section.id); }}><Delete /></IconButton>
                      <IconButton onClick={e => { e.stopPropagation(); handlePreviewSection(section); }}><Visibility /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {selectedSection && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
              <Typography variant="subtitle1">{selectedSection.name}</Typography>
              {selectedSection.description && <Typography variant="body2" sx={{ mb: 1 }}>{selectedSection.description}</Typography>}
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Fields:</Typography>
              <pre style={{ maxWidth: 600, whiteSpace: 'pre-wrap' }}>{JSON.stringify(selectedSection.fields, null, 2)}</pre>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      <Dialog 
        open={previewOpen} 
        onClose={() => setPreviewOpen(false)} 
        maxWidth="sm" 
        fullWidth
        disableRestoreFocus
        disableEnforceFocus
        disableAutoFocus
        keepMounted={false}
      >
        <DialogTitle>Section Preview: {previewSection?.name}</DialogTitle>
        <DialogContent>
          {(() => {
            let fields = [];
            try {
              fields = typeof previewSection?.fields === 'string' ? JSON.parse(previewSection.fields) : previewSection?.fields || [];
            } catch {
              fields = [];
            }
            if (!fields.length) return <Typography>No fields defined.</Typography>;
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {fields.map((field: any, idx: number) => {
                  const dataType = String(field.data_type || '').toLowerCase();
                  if (dataType.includes('dropdown')) {
                    return (
                      <FormControl key={idx} fullWidth disabled>
                        <InputLabel>{field.field_name}</InputLabel>
                        <Select label={field.field_name} value="">
                          <MenuItem value="">Select...</MenuItem>
                          <MenuItem value="Option1">Option 1</MenuItem>
                          <MenuItem value="Option2">Option 2</MenuItem>
                        </Select>
                      </FormControl>
                    );
                  } else if (dataType.includes('file upload')) {
                    return (
                      <Box key={idx}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>{field.field_name}</Typography>
                        <input type="file" style={{ display: 'block', width: '100%' }} disabled />
                      </Box>
                    );
                  } else if (dataType.includes('date/time') || dataType.includes('datetime')) {
                    return (
                      <TextField
                        key={idx}
                        label={field.field_name}
                        type="datetime-local"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        disabled
                      />
                    );
                  } else if (dataType.includes('numeric')) {
                    return (
                      <TextField
                        key={idx}
                        label={field.field_name}
                        type="number"
                        fullWidth
                        disabled
                      />
                    );
                  } else if (["icd_code", "cpt_code", "snomed_code"].includes(dataType)) {
                    let codeType: 'icd' | 'cpt' | 'snomed' = 'icd';
                    if (dataType.includes('cpt_code') || dataType.includes('cpt')) {
                      codeType = 'cpt';
                    } else if (dataType.includes('snomed_code') || dataType.includes('snomed')) {
                      codeType = 'snomed';
                    }
                    
                    return (
                      <Box key={idx} sx={{ mt: 1, mb: 1 }}>
                        <CodeSearchDropdown
                          codeType={codeType}
                          value=""
                          onChange={() => {}}
                          label={field.field_name}
                          disabled={true}
                          placeholder={`Search ${codeType.toUpperCase()} codes...`}
                        />
                      </Box>
                    );
                  } else {
                    return (
                      <TextField
                        key={idx}
                        label={field.field_name}
                        fullWidth
                        disabled
                      />
                    );
                  }
                })}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog 
        open={editFieldsDialogOpen} 
        onClose={() => setEditFieldsDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        keepMounted={false}
      >
        <DialogTitle sx={{ m: 0, p: 2, pr: 5, position: 'relative' }}>
          Edit Section Fields: {editFieldsSection?.name}
          <IconButton
            aria-label="close"
            onClick={() => setEditFieldsDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'row', gap: 2, minHeight: 400 }}>
          {/* Top pane: UI-based editor */}
          <Box sx={{ flex: 1, pr: 2, borderRight: '1px solid #eee' }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Fields</Typography>
            {editFields.map((field, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                <TextField
                  label="Field Name"
                  value={field.field_name}
                  onChange={e => handleFieldChange(idx, 'field_name', e.target.value)}
                  size="small"
                  sx={{ flex: 2 }}
                />
                <FormControl sx={{ flex: 2 }} size="small">
                  <InputLabel>Data Type</InputLabel>
                  <Select
                    value={field.data_type}
                    label="Data Type"
                    onChange={e => handleFieldChange(idx, 'data_type', e.target.value)}
                  >
                    <MenuItem value="numeric">numeric</MenuItem>
                    <MenuItem value="text">text</MenuItem>
                    <MenuItem value="datetime picker">datetime picker</MenuItem>
                    <MenuItem value="dropdown">dropdown</MenuItem>
                    <MenuItem value="ICD_Code">ICD_Code</MenuItem>
                    <MenuItem value="CPT_Code">CPT_Code</MenuItem>
                    <MenuItem value="SNOMED_Code">SNOMED_Code</MenuItem>
                    <MenuItem value="File upload">File upload</MenuItem>
                  </Select>
                </FormControl>
                <FormControl sx={{ flex: 1 }}>
                  <Checkbox
                    checked={!!field.required}
                    onChange={e => handleFieldChange(idx, 'required', e.target.checked)}
                  />
                  <Typography variant="body2">Required</Typography>
                </FormControl>
                <IconButton onClick={() => handleRemoveField(idx)} size="small"><Delete fontSize="small" /></IconButton>
              </Box>
            ))}
            <Button onClick={handleAddField} variant="outlined" size="small" sx={{ mt: 1 }}>Add Field</Button>
          </Box>
          {/* Bottom pane: JSON view */}
          <Box sx={{ flex: 1, pl: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>JSON</Typography>
            <TextField
              value={JSON.stringify(editFields, null, 2)}
              multiline
              minRows={18}
              fullWidth
              InputProps={{ readOnly: true }}
              sx={{ fontFamily: 'monospace', fontSize: 13 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditFieldsDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveFields}>Save</Button>
        </DialogActions>
      </Dialog>
      <Dialog 
        open={classPreviewOpen} 
        onClose={() => setClassPreviewOpen(false)} 
        maxWidth="md" 
        fullWidth
        keepMounted={false}
      >
        <DialogTitle sx={{ m: 0, p: 2, pr: 5, position: 'relative' }}>
          Specimen Class Preview: {classPreview?.label}
          <IconButton
            aria-label="close"
            onClick={() => setClassPreviewOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {classPreview && (classPreview.sections || []).length === 0 && (
            <Typography>No sections defined for this class.</Typography>
          )}
          {classPreview && (classPreview.sections || []).map((sectionId: any) => {
            const section = sections.find(s => s.id === sectionId);
            if (!section) return null;
            let fields = [];
            try {
              fields = typeof section.fields === 'string' ? JSON.parse(section.fields) : section.fields || [];
            } catch {
              fields = [];
            }
            return (
              <Box key={section.id} sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>{section.name}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {fields.length === 0 && <Typography variant="body2">No fields defined.</Typography>}
                  {fields.map((field: any, idx: number) => {
                    const dataType = String(field.data_type || '').toLowerCase();
                    if (dataType.includes('dropdown')) {
                      return (
                        <FormControl key={idx} fullWidth>
                          <InputLabel>{field.field_name}</InputLabel>
                          <Select label={field.field_name} defaultValue="">
                            <MenuItem value="">Select...</MenuItem>
                            <MenuItem value="Option1">Option 1</MenuItem>
                            <MenuItem value="Option2">Option 2</MenuItem>
                          </Select>
                        </FormControl>
                      );
                    } else if (dataType.includes('file upload')) {
                      return (
                        <Box key={idx}>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>{field.field_name}</Typography>
                          <input type="file" style={{ display: 'block', width: '100%' }} />
                        </Box>
                      );
                    } else if (dataType.includes('date/time') || dataType.includes('datetime')) {
                      return (
                        <TextField
                          key={idx}
                          label={field.field_name}
                          type="datetime-local"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      );
                    } else if (["icd_code", "cpt_code", "snomed_code"].includes(dataType)) {
                      let codeType: 'icd' | 'cpt' | 'snomed' = 'icd';
                      if (dataType.includes('cpt_code') || dataType.includes('cpt')) {
                        codeType = 'cpt';
                      } else if (dataType.includes('snomed_code') || dataType.includes('snomed')) {
                        codeType = 'snomed';
                      }
                      
                      return (
                        <Box key={idx} sx={{ mt: 1, mb: 1 }}>
                          <CodeSearchDropdown
                            codeType={codeType}
                            value=""
                            onChange={() => {}}
                            label={field.field_name}
                            disabled={true}
                            placeholder={`Search ${codeType.toUpperCase()} codes...`}
                          />
                        </Box>
                      );
                    } else {
                      return (
                        <TextField
                          key={idx}
                          label={field.field_name}
                          fullWidth
                        />
                      );
                    }
                  })}
                </Box>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClassPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog 
        open={reportTemplateDialogOpen} 
        onClose={() => setReportTemplateDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        disableRestoreFocus
        disableEnforceFocus
        disableAutoFocus
        keepMounted={false}
      >
        <DialogTitle sx={{ m: 0, p: 2, pr: 5, position: 'relative' }}>
          Manage Report Templates
          <IconButton
            aria-label="close"
            onClick={() => setReportTemplateDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={() => handleOpenReportTemplateForm()}>Add Report Template</Button>
          </Box>
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apCaseTemplates.map((tpl: any) => (
                  <TableRow key={tpl.id}>
                    <TableCell>{tpl.name}</TableCell>
                    <TableCell>{tpl.description}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleOpenReportTemplateForm(tpl)}><Edit /></IconButton>
                      <IconButton onClick={() => handleDeleteReportTemplate(tpl)}><Delete /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Dialog 
            open={reportTemplateFormOpen} 
            onClose={handleCloseReportTemplateForm}
            keepMounted={false}
          >
            <DialogTitle sx={{ m: 0, p: 2, pr: 5, position: 'relative' }}>
              {editingReportTemplate ? 'Edit' : 'Add'} Report Template
              <IconButton
                aria-label="close"
                onClick={handleCloseReportTemplateForm}
                sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField label="Name" name="name" value={reportTemplateForm.name} onChange={handleReportTemplateFormChange} fullWidth required />
              <TextField label="Description" name="description" value={reportTemplateForm.description} onChange={handleReportTemplateFormChange} fullWidth />
              {reportTemplateError && <Typography color="error">{reportTemplateError}</Typography>}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseReportTemplateForm}>Cancel</Button>
              <Button variant="contained" onClick={handleSaveReportTemplate}>{editingReportTemplate ? 'Update' : 'Create'}</Button>
            </DialogActions>
          </Dialog>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportTemplateDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog 
        open={partsListDialogOpen} 
        onClose={handleClosePartsListDialog} 
        maxWidth="sm" 
        fullWidth
        keepMounted={false}
      >
        <DialogTitle sx={{ m: 0, p: 2, pr: 5, position: 'relative' }}>
          Specimen Parts
          <IconButton
            aria-label="close"
            onClick={handleClosePartsListDialog}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Button variant="contained" onClick={handleAddPart} sx={{ mb: 2 }}>Add Specimen Part</Button>
          {allParts.length === 0 ? (
            <Typography>No specimen parts defined.</Typography>
          ) : (
            allParts.map(part => (
              <Box key={part.id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ flex: 1 }}>{part.partName}</Typography>
                <Button size="small" onClick={() => handleEditPart(part)} sx={{ mr: 1 }}>Edit</Button>
                <Button size="small" color="error" onClick={() => handleDeletePart(part.id)}>Delete</Button>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePartsListDialog}>Close</Button>
        </DialogActions>
      </Dialog>
      <Drawer 
        anchor="right" 
        open={partDrawerOpen} 
        onClose={handleClosePartsDialog}
        keepMounted={false}
      >
        <Box sx={{ width: 400, p: 3 }}>
          <Typography variant="h6" gutterBottom>{editingPart ? 'Edit Specimen Part' : 'Add Specimen Part'}</Typography>
          <TextField label="Name" name="part_name" value={partForm.part_name} onChange={handlePartFormChange} fullWidth sx={{ mb: 2 }} />
          <TextField label="Description" name="description" value={partForm.description} onChange={handlePartFormChange} fullWidth sx={{ mb: 2 }} />
          <Autocomplete
            multiple
            options={collectionMethods}
            value={partForm.collection_method || []}
            onChange={(_, value) => setPartForm(f => ({ ...f, collection_method: value as string[] }))}
            renderInput={params => (
              <TextField {...params} label="Collection Method" fullWidth sx={{ mb: 2 }} />
            )}
            freeSolo
          />
          {partError && <Typography color="error">{partError}</Typography>}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button onClick={handleClosePartsDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleSavePart}>{editingPart ? 'Save' : 'Add'}</Button>
          </Box>
        </Box>
      </Drawer>
      <Dialog 
        open={reorderDialogOpen} 
        onClose={() => setReorderDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        keepMounted={false}
      >
        <DialogTitle>Change Section Order</DialogTitle>
        <DialogContent sx={{ minWidth: 400 }}>
          <List>
            {reorderSections.map((section, idx) => (
              <ListItem key={section.id} secondaryAction={
                <Box>
                  <IconButton size="small" onClick={() => moveSection(idx, -1)} disabled={idx === 0}><ArrowUpward /></IconButton>
                  <IconButton size="small" onClick={() => moveSection(idx, 1)} disabled={idx === reorderSections.length - 1}><ArrowDownward /></IconButton>
                </Box>
              }>
                <ListItemText primary={section.name} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReorderDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveReorder}>Save Order</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SpecimenClassAdmin; 